<template>
  <div v-if="valid">
    <v-timeline>
      <template v-for="activity in items">
        <v-timeline-item class="mb-4" color="grey" icon-color="grey lighten-2" small :key="activity.id" right>
          <template v-slot:opposite>
            <span>
              {{ activity.createdDate | formatDateClient("MM/DD/YYYY hh:mm a z", selectedClient) }}<br />
              ({{ activity.createdDate | formatDateFromNow }})
              <span v-if="activity.user"> <br />by {{ activity.user.fullName }}</span>
            </span>
          </template>
          <v-card class="elevation-2">
            <v-card-text>
              <template v-if="activity.hasListTree">
                <v-treeview
                  :items="activity.messageTree"
                  item-key="id"
                  item-text="label"
                  open-on-click
                  dense
                  hoverable
                  transition
                >
                  <template v-slot:label="{ item }">
                    <p class="mb-0">{{ item.label }}</p>
                  </template>
                </v-treeview>
              </template>
              <span v-else v-html="activity.message"></span>
            </v-card-text>
          </v-card>
        </v-timeline-item>
      </template>
      <v-timeline-item class="mb-4" hide-dot v-if="items.length > 0">
        <v-btn v-if="!isLastPage" color="accent" elevation="2" rounded text @click="showMore">Show more</v-btn>
      </v-timeline-item>
    </v-timeline>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ApiService from "../../services/api.service";

export default {
  name: "ActivityTimeline",
  props: {
    type: String,
    id: Number
  },
  data: () => ({
    validType: false,
    validId: false,
    dialog: false,
    loading: false,
    itemsNumber: 10,
    isLastPage: false,
    pageNumber: 0,
    items: [],
    treeNodeId: 0
  }),

  computed: {
    ...mapGetters(["selectedParticipant", "selectedClient"]),
    valid() {
      return this.validType && this.validId;
    }
  },

  mounted() {
    this.fetchData();
  },

  methods: {
    fetchData() {
      if (!this.valid) {
        return;
      }
      this.loading = true;

      let filters = {};
      if (this.search && this.search.length > 0) {
        filters.keyword = this.search;
      }
      if (this.id) {
        filters[this.type] = { id: this.id };
      }
      const params = new URLSearchParams();
      params.append("size", this.itemsNumber);
      params.append("page", this.pageNumber);
      params.append("sort", "createdDate,DESC");
      if (["productRegistration", "productSerialNumber"].includes(this.type)) {
        params.append("expand", "user");
      }
      return ApiService.post(`/api/activities/search/${this.type}s?${params.toString()}`, filters)
        .then(({ data }) => {
          this.isLastPage = data.totalPages == this.pageNumber + 1;
          const parsedItems = data.content.map(activity => this.mapActivityMessage(activity));
          this.items = this.items.concat(parsedItems);
          return Promise.resolve(data);
        })
        .catch(e => {
          return Promise.reject(e);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    showMore() {
      this.pageNumber += 1;
      this.fetchData();
    },

    mapActivityMessage(activity) {
      const message = (activity.message || "").toString();
      if (!this.hasListMarkup(message)) {
        return {
          ...activity,
          hasListTree: false,
          messageTree: []
        };
      }

      const messageTree = this.parseMessageTree(message);
      if (messageTree.length === 0) {
        return {
          ...activity,
          hasListTree: false,
          messageTree: []
        };
      }

      return {
        ...activity,
        hasListTree: true,
        messageTree
      };
    },

    hasListMarkup(message) {
      return /<\s*(ul|ol)\b/i.test(message);
    },

    parseMessageTree(message) {
      try {
        const parser = new DOMParser();
        const documentNode = parser.parseFromString(`<div>${message}</div>`, "text/html");
        const container = documentNode.body.firstElementChild;

        if (!container) {
          return [];
        }

        return this.parseElementSequence(Array.from(container.children));
      } catch (e) {
        return [];
      }
    },
    parseElementSequence(elements) {
      const nodes = [];
      let lastNode = null;

      elements.forEach(element => {
        if (["UL", "OL"].includes(element.tagName)) {
          const parsedChildren = this.parseListElement(element);
          if (lastNode) {
            lastNode.children = lastNode.children.concat(parsedChildren);
            return;
          }
          nodes.push(...parsedChildren);
          return;
        }

        if (element.tagName !== "LI") {
          return;
        }

        const node = {
          id: this.nextTreeNodeId(),
          label: this.parseListItemLabel(element) || "Details",
          children: []
        };

        const nestedLists = Array.from(element.children).filter(child => {
          return ["UL", "OL"].includes(child.tagName);
        });
        if (nestedLists.length > 0) {
          node.children = nestedLists.flatMap(list => this.parseListElement(list));
        }

        nodes.push(node);
        lastNode = node;
      });

      return nodes;
    },
    parseListElement(listElement) {
      return this.parseElementSequence(Array.from(listElement.children));
    },
    parseListItemLabel(listItem) {
      const labelParts = [];

      Array.from(listItem.childNodes).forEach(node => {
        if (node.nodeType === Node.TEXT_NODE) {
          labelParts.push(node.textContent || "");
          return;
        }

        if (node.nodeType === Node.ELEMENT_NODE && !["UL", "OL"].includes(node.tagName)) {
          labelParts.push(node.textContent || "");
        }
      });

      return labelParts
        .join(" ")
        .replace(/\s+/g, " ")
        .trim();
    },
    nextTreeNodeId() {
      this.treeNodeId += 1;
      return `activity-tree-node-${this.treeNodeId}`;
    }
  },
  watch: {
    type: {
      immediate: true,
      handler(v) {
        switch (v) {
          case "approval":
          case "claim":
          case "payout":
          case "promotion":
          case "sale":
          case "storeOrder":
          case "participant":
          case "productRegistration":
          case "productSerialNumber":
            this.validType = true;
            break;
          default:
            this.validType = false;
            console.log("No activity type found");
            break;
        }
      }
    },
    id: {
      immediate: true,
      handler(v) {
        if (!v || isNaN(v)) {
          this.validId = false;
        }
        this.validId = true;
      }
    }
  }
};
</script>

<style scoped>
::v-deep .v-timeline::before {
  left: 247px !important;
}

::v-deep .v-timeline-item__opposite {
  flex: 0 0 200px !important;
  max-width: 200px !important;
}

::v-deep .v-timeline-item__body {
  max-width: calc(100% - 296px) !important;
}

::v-deep .v-treeview-node__label {
  white-space: normal !important;
}
</style>
